import type { ComponentType, RefObject } from "react";
import { useEffect, useMemo, useState } from "react";
import * as RGL from "react-grid-layout";

import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";

import type { AccountType } from "@/types/api";
import type {
  BreakpointKey,
  GridLayoutItem,
  SizeVariant,
  WidgetDefinition,
} from "./types";
import { BREAKPOINT_KEYS } from "./types";
import { WidgetFrame, availableSizesForWidget } from "./WidgetFrame";
import { WidgetLibrary } from "./WidgetLibrary";
import { useLayoutState, resolveSize } from "./useLayoutState";

interface ResponsiveGridLayoutProps {
  layouts: Record<string, GridLayoutItem[]>;
  breakpoints: Record<string, number>;
  cols: Record<string, number>;
  rowHeight: number;
  margin: [number, number];
  containerPadding: [number, number];
  isDraggable: boolean;
  isResizable: boolean;
  draggableHandle: string;
  onLayoutChange: (
    current: GridLayoutItem[],
    all: Record<string, GridLayoutItem[]>,
  ) => void;
  onBreakpointChange: (bp: string, cols: number) => void;
  compactType?: "vertical" | "horizontal" | null;
  preventCollision?: boolean;
  className?: string;
  measureBeforeMount?: boolean;
  width: number;
  children?: React.ReactNode;
}

const rglModule = RGL as unknown as {
  ResponsiveGridLayout: ComponentType<ResponsiveGridLayoutProps>;
  useContainerWidth: (options?: {
    measureBeforeMount?: boolean;
    initialWidth?: number;
  }) => {
    containerRef: RefObject<HTMLDivElement>;
    width: number;
    mounted: boolean;
  };
};
const ResponsiveGridLayout = rglModule.ResponsiveGridLayout;
const useContainerWidth = rglModule.useContainerWidth;

// Multi-resolution grid. Each breakpoint has its own saved layout, so a user
// can arrange widgets independently for phone / small laptop / desktop / wide /
// ultrawide monitors.
const BREAKPOINTS: Record<BreakpointKey, number> = {
  "2xl": 1920,
  xl: 1440,
  lg: 900,
  md: 600,
  sm: 0,
};
// Column counts are chosen as multiples of 6 so XS (w=3) and M (w=6)
// widgets pack evenly without leaving a dead gutter stripe on the right.
const COLS: Record<BreakpointKey, number> = {
  "2xl": 24,
  xl: 18,
  lg: 12,
  md: 12,
  sm: 4,
};
const ROW_HEIGHT = 72;
const MARGIN: [number, number] = [12, 12];

interface PersonalizableDashboardProps {
  pageKey: string;
  widgets: WidgetDefinition[];
  accountType: AccountType;
  defaultActiveTypes?: string[];
  editing: boolean;
  onEditingChange: (editing: boolean) => void;
  /** Page title (uppercase, left-aligned in the header). */
  title: string;
  /** Optional content rendered between the page header and the widget grid
   *  (e.g. a FilterBar that controls the data feeding the widgets). */
  toolbar?: React.ReactNode;
}

function CustomizeIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="14"
      height="14"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </svg>
  );
}

export function PersonalizableDashboard({
  pageKey,
  widgets,
  accountType,
  defaultActiveTypes,
  editing,
  onEditingChange,
  title,
  toolbar,
}: PersonalizableDashboardProps) {
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [breakpoint, setBreakpoint] = useState<BreakpointKey>("lg");
  const { containerRef, width } = useContainerWidth({ initialWidth: 1200 });

  const visibleWidgets = useMemo(
    () =>
      widgets.filter(
        (def) =>
          !def.roles || def.roles.length === 0 || def.roles.includes(accountType),
      ),
    [widgets, accountType],
  );

  const widgetDefs = useMemo(() => {
    const defs: Record<string, WidgetDefinition> = {};
    for (const def of visibleWidgets) {
      defs[def.type] = def;
    }
    return defs;
  }, [visibleWidgets]);

  const resolvedDefaultActive = useMemo(() => {
    if (defaultActiveTypes && defaultActiveTypes.length) {
      return defaultActiveTypes.filter((type) => widgetDefs[type]);
    }
    return visibleWidgets
      .filter((def) => !def.hideFromLibrary)
      .slice(0, 8)
      .map((def) => def.type);
  }, [defaultActiveTypes, visibleWidgets, widgetDefs]);

  const {
    layouts,
    activeTypes,
    config,
    isReady,
    handleLayoutChange,
    addWidget,
    removeWidget,
    setWidgetSize,
    resetLayout,
  } = useLayoutState({
    pageKey,
    widgetDefs,
    defaultActiveTypes: resolvedDefaultActive,
    cols: COLS,
  });

  // Close library automatically when editing disables
  useEffect(() => {
    if (!editing) setLibraryOpen(false);
  }, [editing]);

  // Nudge every chart to re-measure after RGL swaps layouts on viewport
  // resize. Without this, the per-chart ResizeObserver occasionally misses
  // the size change (races with RGL's async width pass) and widgets render
  // squashed until the user clicks "Customize" and forces a re-layout.
  // Fires on the next frame so RGL has finished applying the new widths.
  useEffect(() => {
    const raf = requestAnimationFrame(() => {
      window.dispatchEvent(new Event("resize"));
    });
    return () => cancelAnimationFrame(raf);
  }, [width, breakpoint]);

  // Freeze per-item when not editing so RGL can't move/resize items, even if
  // some stale handler is still attached. Strip these flags on persistence.
  const frozenLayouts = useMemo(() => {
    if (!layouts) return null;
    const apply = (items: GridLayoutItem[]) =>
      items.map((item) => ({
        ...item,
        static: !editing,
        isDraggable: editing,
        isResizable: false,
      }));
    const result: Record<string, GridLayoutItem[]> = {};
    for (const bp of BREAKPOINT_KEYS) {
      result[bp] = apply(layouts[bp] ?? []);
    }
    return result;
  }, [layouts, editing]);

  const isMobile = breakpoint === "sm";
  const ready = isReady && !!layouts && !!frozenLayouts;

  return (
    <div
      className={`personalizable-dashboard ${editing ? "personalizable-dashboard--editing" : ""}`}
    >
      <div className="page-header page-header--tight">
        <h1 className="page-header__title">{title}</h1>
        <div className="page-header__actions">
          {ready && editing && (
            <>
              <span
                className="dashboard-edit-hint"
                aria-hidden="true"
                title="Drag widgets to reorder · S/M/L in widget headers to resize"
              >
                Drag to reorder · S/M/L to resize
              </span>
              <button
                type="button"
                className="button ghost compact"
                onClick={() => setLibraryOpen((prev) => !prev)}
              >
                {libraryOpen ? "Hide widgets" : "Add widgets"}
              </button>
              <button
                type="button"
                className="button ghost compact"
                onClick={resetLayout}
              >
                Reset
              </button>
            </>
          )}
          <button
            type="button"
            className={`button compact ${editing ? "primary" : "ghost"}`}
            onClick={() => onEditingChange(!editing)}
          >
            <CustomizeIcon />
            <span>{editing ? "Done" : "Customize"}</span>
          </button>
        </div>
      </div>

      {toolbar && <div className="personalizable-dashboard__toolbar">{toolbar}</div>}


      {/*
        Canvas is mounted unconditionally (even while loading layouts) so that
        `useContainerWidth`'s ResizeObserver can attach on first render. If the
        canvas is rendered behind a loading gate, the observer never attaches
        and `width` stays stuck at `initialWidth`, which breaks breakpoint
        detection (xl / 2xl never trigger).
      */}
      <div
        className="personalizable-dashboard__canvas"
        ref={containerRef}
      >
        {ready ? (
          <>
            <ResponsiveGridLayout
              className="personalizable-dashboard__grid"
              layouts={frozenLayouts as unknown as Record<string, GridLayoutItem[]>}
              breakpoints={BREAKPOINTS}
              cols={COLS}
              rowHeight={ROW_HEIGHT}
              margin={MARGIN}
              containerPadding={[0, 0]}
              isDraggable={editing && !isMobile}
              isResizable={false}
              draggableHandle=".widget-drag-handle"
              onLayoutChange={handleLayoutChange}
              onBreakpointChange={(bp: string) =>
                setBreakpoint(bp as BreakpointKey)
              }
              compactType="vertical"
              preventCollision={false}
              width={width}
            >
              {activeTypes.map((type) => {
                const def = widgetDefs[type];
                if (!def) return null;
                const variant =
                  config[type]?.sizeVariant ?? def.defaultSize ?? "m";
                const size = resolveSize(def, variant);
                const Component = def.Component;
                return (
                  <div key={type} className="personalizable-dashboard__item">
                    <WidgetFrame
                      title={def.title}
                      editing={editing}
                      sizeVariant={size.variant}
                      availableSizes={availableSizesForWidget(def)}
                      onChangeSize={(v: SizeVariant) => setWidgetSize(type, v)}
                      onRemove={() => removeWidget(type)}
                      Icon={def.Icon}
                      required={def.required}
                    >
                      <Component
                        instanceId={type}
                        sizeVariant={size.variant}
                        breakpoint={breakpoint}
                        editing={editing}
                      />
                    </WidgetFrame>
                  </div>
                );
              })}
            </ResponsiveGridLayout>

            {editing && libraryOpen && (
              <WidgetLibrary
                widgetDefs={widgetDefs}
                activeTypes={activeTypes}
                onAdd={addWidget}
                onClose={() => setLibraryOpen(false)}
              />
            )}
          </>
        ) : (
          <p className="empty-state">Loading dashboard…</p>
        )}
      </div>
    </div>
  );
}
